/**
 * Wheel-Eingabe mit Trackpad-Momentum-Erkennung.
 *
 * Ereignisse mit weniger als GESTURE_GAP ms Abstand gelten als eine Geste.
 * Pro Geste wird höchstens einmal navigiert; der Rest (Momentum) wird
 * verschluckt, damit ein Trackpad-Wisch nicht mehrere Sektionen springt.
 */

import { normalizeWheelDelta } from '../utils.js';

const GESTURE_GAP = 80;
const THRESHOLD = 60;

export class WheelHandler {
	/**
	 * @param {Element} target Element, auf dem gelauscht wird.
	 * @param {Object}  cb     Callbacks:
	 *   verdict(event)   → 'native' | 'block' | 'navigate'
	 *   isLocked()       → bool (Übergang läuft)
	 *   onNavigate(dir)  → void (dir: 1 | -1)
	 */
	constructor( target, cb ) {
		this.target = target;
		this.cb = cb;
		this.acc = 0;
		this.lastTime = 0;
		this.consumed = false;
		this.handle = this.handle.bind( this );

		target.addEventListener( 'wheel', this.handle, { passive: false } );
	}

	handle( event ) {
		// Hinweis: Events aus horizontalen Slidern kommen nur hier an, wenn
		// der Slider sie nicht konsumiert hat (stopPropagation am Slider) —
		// das ist der Boundary-Handoff an die vertikale Engine.
		const verdict = this.cb.verdict( event );

		if ( verdict === 'native' ) {
			return;
		}

		event.preventDefault();

		if ( verdict === 'block' ) {
			return;
		}

		const now = performance.now();
		const isNewGesture = now - this.lastTime > GESTURE_GAP;
		this.lastTime = now;

		if ( isNewGesture ) {
			this.consumed = false;
			this.acc = 0;
		}

		if ( this.consumed ) {
			return;
		}

		if ( this.cb.isLocked() ) {
			// Geste während eines laufenden Übergangs komplett verschlucken.
			this.consumed = true;
			return;
		}

		this.acc += normalizeWheelDelta( event );

		if ( Math.abs( this.acc ) >= THRESHOLD ) {
			const direction = this.acc > 0 ? 1 : -1;
			this.consumed = true;
			this.acc = 0;
			this.cb.onNavigate( direction );
		}
	}

	destroy() {
		this.target.removeEventListener( 'wheel', this.handle );
	}
}
